import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import Header from "@/components/Header";
import AppSidebar from "@/components/AppSidebar";
import { GlobalInfluencerFilterProvider } from "@/contexts/GlobalInfluencerFilterContext";

// Eagerly import high-traffic pages to prevent blank flashes
import HomePage from "./pages/HomePage";
import DashboardPage from "./pages/DashboardPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ProfilePage from "./pages/ProfilePage";

// Lazy-load infrequent pages
const InfluencerLandingPage = lazy(() => import("./pages/InfluencerLandingPage"));
const ThumbnailPage = lazy(() => import("./pages/ThumbnailPage"));
const AssetPage = lazy(() => import("./pages/AssetPage"));
const AssetResearchPage = lazy(() => import("./pages/AssetResearchPage"));

const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const OnboardPage = lazy(() => import("./pages/OnboardPage"));
const BrokeragePage = lazy(() => import("./pages/BrokeragePage"));
const LogoPage = lazy(() => import("./pages/LogoPage"));
const ThesisBuilderPage = lazy(() => import("./pages/ThesisBuilderPage"));
const AssetAssessmentPage = lazy(() => import("./pages/AssetAssessmentPage"));
const ValuationPage = lazy(() => import("./pages/ValuationPage"));
const ThesisDetailPage = lazy(() => import("./pages/ThesisDetailPage"));
const FiltersIndexPage = lazy(() => import("./pages/FiltersIndexPage"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

const AppLayout = () => {
  const { user } = useAuth();
  
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <div className="md:hidden sticky top-0 z-50 flex items-center h-12 border-b border-border bg-background/80 backdrop-blur-md px-3">
            <SidebarTrigger />
            <span className="ml-2 font-mono font-bold text-sm text-primary tracking-wider">FINFLUENCER AUDIT</span>
          </div>
          <Header />
          <Routes>
            {/* Eagerly-loaded routes – no Suspense needed */}
            <Route path="/" element={<DashboardPage />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/legacy-main" element={<HomePage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/profile/:handle" element={<ProfilePage />} />

            {/* Lazy-loaded routes – wrapped in Suspense */}
            <Route path="/research" element={<Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}><AssetResearchPage /></Suspense>} />
            <Route path="/profile/:handle/thumbnail" element={<Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}><ThumbnailPage /></Suspense>} />
            <Route path="/asset/:ticker" element={<Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}><AssetPage /></Suspense>} />
            <Route path="/auth/callback" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><AuthCallback /></Suspense>} />
            <Route path="/login" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><LoginPage /></Suspense>} />
            <Route path="/for-influencers" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><InfluencerLandingPage /></Suspense>} />
            <Route path="/onboard" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><OnboardPage /></Suspense>} />
            <Route path="/onboard/brokerage" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><BrokeragePage /></Suspense>} />
            <Route path="/filters-index" element={<Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}><FiltersIndexPage /></Suspense>} />
            <Route path="/thesis" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><ThesisBuilderPage /></Suspense>} />
            <Route path="/thesis/:id" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><ThesisDetailPage /></Suspense>} />
            <Route path="/valuation/lifecycle" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><ValuationPage /></Suspense>} />
            <Route path="/valuation" element={<Navigate to="/valuation/lifecycle" replace />} />
            <Route path="/assessment" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><AssetAssessmentPage /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><AdminDashboard /></Suspense>} />
            <Route path="/.lovable/oauth/consent" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><OAuthConsent /></Suspense>} />
            <Route path="/logos" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><LogoPage /></Suspense>} />
            <Route path="*" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><NotFound /></Suspense>} />
          </Routes>
        </div>
      </div>
    </SidebarProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <GlobalInfluencerFilterProvider>
            <AppLayout />
          </GlobalInfluencerFilterProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
